import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io, Socket } from "socket.io-client";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { updateTask as updateTaskInStore } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";
import { taskAndSubtaskActivitySlice } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks-activity-slice";

import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { ToDoPriority } from "../enum/ToDoPriority.enum";
import {
	CreateSubTaskInfo,
	CreateTaskInfo,
	SubTaskInfo,
	TaskInfo,
	UpdateSubTaskInfo,
	UpdateTaskInfo,
} from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";
import { CreateTaskAndSubtaskComment } from "../../../TasksAndSubTasks/schema/tasks-and-subtasks-activity";

interface SocketContextType {
	socket: Socket | null;
	changeTaskStatus: (taskId: number, newStatus: ToDoStatus) => void;
	changePriority: (taskId: number, newPriority: ToDoPriority) => void;
	changeSubTaskStatus: (taskId: number, subTaskId: number, newStatus: ToDoStatus) => void;
	assignAssignee: (taskId: number, assigneeId: number | null) => void;
	assignSubTaskAssignee: (taskId: number, subTaskId: number, assigneeId: number | null) => void;
	createComment: (data: CreateTaskAndSubtaskComment) => void;
	updateComment: (id: number, content: string) => void;
	deleteComment: (id: number) => void;
	createTask: (data: CreateTaskInfo) => void;
	updateTask: (taskId: number, data: UpdateTaskInfo) => void;
	createSubTask: (taskId: number, data: CreateSubTaskInfo) => void;
	updateSubTask: (taskId: number, subTaskId: number, data: UpdateSubTaskInfo) => void;
	deleteTask: (taskId: number) => void;
	deleteSubTask: (taskId: number, subTaskId: number) => void;
}

export enum ToDoSocketAction {
	JOIN_ROOM = "join-room",
	CHANGE_STATUS = "change-status",
	ACTUAL_DATA = "actual-data",
	ERROR = "error",
	CHANGE_SUB_TASK_STATUS = "change-sub-task-status",
	CHANGE_PRIORITY = "change-priority",
	ASSIGN_ASSIGNEE = "assign-assignee",
	ASSIGN_SUB_TASK_ASSIGNEE = "assign-sub-task-assignee",
	CREATE_COMMENT = "create-comment",
	UPDATE_COMMENT = "update-comment",
	DELETE_COMMENT = "delete-comment",
	CREATE_TASK = "create-task",
	UPDATE_TASK = "update-task",
	CREATE_SUBTASK = "create-subtask",
	UPDATE_SUBTASK = "update-subtask",
	DELETE_TASK = "delete-task",
	DELETE_SUBTASK = "delete-subtask",
}

const ToDoSocketContext = createContext<SocketContextType | undefined>(undefined);

export const ToDoSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const companyId = auth?.user?.company?.id;
	const dispatch = useDispatch();

	useEffect(() => {
		if (!token || !companyId) {
			return;
		}

		const newSocket = io(import.meta.env.VITE_API_URL, {
			query: { token },
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 10000,
			timeout: 10000,
			transports: ["websocket"],
		});

		newSocket.on("connect_error", (error) => {
			console.error("Socket connection error:", error);
		});

		newSocket.on("disconnect", (reason) => {
			console.error("Socket disconnected:", reason);
		});

		setSocket(newSocket);

		newSocket.emit(ToDoSocketAction.JOIN_ROOM, companyId);

		newSocket.on(
			ToDoSocketAction.ACTUAL_DATA,
			(data: { tasks: TaskInfo[]; subtasks: SubTaskInfo[] }) => {
				dispatch(updateTaskInStore(data));
			}
		);

		newSocket.on(ToDoSocketAction.CREATE_COMMENT, (comment) => {
			dispatch(taskAndSubtaskActivitySlice.actions.addComment(comment));
		});

		newSocket.on(ToDoSocketAction.UPDATE_COMMENT, (comment) => {
			dispatch(taskAndSubtaskActivitySlice.actions.updateComment(comment));
		});

		newSocket.on(ToDoSocketAction.DELETE_COMMENT, ({ id }) => {
			dispatch(taskAndSubtaskActivitySlice.actions.removeComment(id));
		});

		return () => {
			newSocket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, companyId]);

	const changeTaskStatus = useCallback(
		(taskId: number, newStatus: ToDoStatus) => {
			if (socket) {
				socket.emit(ToDoSocketAction.CHANGE_STATUS, { taskId, newStatus });
			}
		},
		[socket]
	);

	const changePriority = useCallback(
		(taskId: number, newPriority: ToDoPriority) => {
			if (socket) {
				socket.emit(ToDoSocketAction.CHANGE_PRIORITY, { taskId, newPriority });
			}
		},
		[socket]
	);

	const changeSubTaskStatus = useCallback(
		(taskId: number, subTaskId: number, newStatus: ToDoStatus) => {
			if (socket) {
				socket.emit(ToDoSocketAction.CHANGE_SUB_TASK_STATUS, {
					taskId,
					subTaskId,
					newStatus,
				});
			}
		},
		[socket]
	);

	const assignAssignee = useCallback(
		(taskId: number, assigneeId: number | null) => {
			if (socket) {
				socket.emit(ToDoSocketAction.ASSIGN_ASSIGNEE, {
					taskId,
					assigneeId,
				});
			}
		},
		[socket]
	);

	const assignSubTaskAssignee = useCallback(
		(taskId: number, subTaskId: number, assigneeId: number | null) => {
			if (socket) {
				socket.emit(ToDoSocketAction.ASSIGN_SUB_TASK_ASSIGNEE, {
					taskId,
					subTaskId,
					assigneeId,
				});
			}
		},
		[socket]
	);

	const createComment = useCallback(
		(data: CreateTaskAndSubtaskComment) => {
			if (socket) {
				socket.emit(ToDoSocketAction.CREATE_COMMENT, data);
			}
		},
		[socket]
	);

	const updateComment = useCallback(
		(id: number, content: string) => {
			if (socket) {
				socket.emit(ToDoSocketAction.UPDATE_COMMENT, { id, content });
			}
		},
		[socket]
	);

	const deleteComment = useCallback(
		(id: number) => {
			if (socket) {
				socket.emit(ToDoSocketAction.DELETE_COMMENT, { id });
			}
		},
		[socket]
	);

	const createTask = useCallback(
		(data: CreateTaskInfo) => {
			if (socket) {
				socket.emit(ToDoSocketAction.CREATE_TASK, data);
			}
		},
		[socket]
	);

	const updateTask = useCallback(
		(taskId: number, data: UpdateTaskInfo) => {
			if (socket) {
				socket.emit(ToDoSocketAction.UPDATE_TASK, { taskId, ...data });
			}
		},
		[socket]
	);

	const createSubTask = useCallback(
		(taskId: number, data: CreateSubTaskInfo) => {
			if (socket) {
				const { taskId: _, ...rest } = data;

				socket.emit(ToDoSocketAction.CREATE_SUBTASK, { taskId, ...rest });
			}
		},
		[socket]
	);

	const updateSubTask = useCallback(
		(taskId: number, subTaskId: number, data: UpdateSubTaskInfo) => {
			if (socket) {
				const { taskId: _, ...rest } = data;

				socket.emit(ToDoSocketAction.UPDATE_SUBTASK, { taskId, subTaskId, ...rest });
			}
		},
		[socket]
	);

	const deleteTask = useCallback(
		(taskId: number) => {
			if (socket) {
				socket.emit(ToDoSocketAction.DELETE_TASK, { taskId });
			}
		},
		[socket]
	);

	const deleteSubTask = useCallback(
		(taskId: number, subTaskId: number) => {
			if (socket) {
				socket.emit(ToDoSocketAction.DELETE_SUBTASK, { taskId, subTaskId });
			}
		},
		[socket]
	);

	return (
		<ToDoSocketContext.Provider
			value={{
				socket,
				changeTaskStatus,
				changePriority,
				changeSubTaskStatus,
				assignAssignee,
				assignSubTaskAssignee,
				createComment,
				updateComment,
				deleteComment,
				createTask,
				updateTask,
				createSubTask,
				updateSubTask,
				deleteTask,
				deleteSubTask,
			}}
		>
			{children}
		</ToDoSocketContext.Provider>
	);
};

export const useToDoSocket = () => {
	const context = useContext(ToDoSocketContext);

	if (!context) {
		throw new Error("useSocket must be used within a SocketProvider");
	}

	return context;
};
